import { AlertTriangle, Info } from 'lucide-react';
import { useI18n, type TranslationKey } from '../lib/i18n';
import type { Period } from '../lib/period';
import type { MoneyLine, Notice, Suppressed } from '../lib/types';
import { Badge, type BadgeTone } from './ui';
import { TrustBadge } from './kit';
import { useDay, useMonthName } from './a-common';

/**
 * Finance's figures as the Executive Dashboard's API sends them (apps/backend/src/modules/executive/
 * executive-finance.service.ts) — an adapter on Finance's own reports — and the pieces that show them:
 * its trust status, its notices with their details, and each month's coverage.
 */

// ---- shapes -------------------------------------------------------------------------------------

export interface ClientRef {
  id: string;
  code: string;
  companyName: string;
  companyNameAr: string | null;
}

export interface ProjectRef {
  id: string;
  code: string;
  name: string;
  nameAr?: string | null;
}

/** Finance's coverage of one month (domain/financial-reports.ts `MonthCoverage`). */
export interface MonthCoverage {
  month: string;
  standing: 'AFTER_CUTOVER' | 'BEFORE_CUTOVER' | 'NO_CUTOVER';
  partialMonth: boolean;
  revenue: 'RECORDED' | 'NO_RECORDS' | 'NOT_PROVIDED';
  salaries: string;
  otherExpenses: string;
  historical: { salaries: string[]; expenses: string[] };
  drafts: number;
  attachments: number;
  missingDocuments: number;
  profit: 'COMPLETE' | 'PARTIAL' | 'NOT_RECONCILED';
}

export interface Cutover {
  date: string | null;
  status: 'NONE' | 'DRAFT' | 'CONFIRMED';
  confirmedAt: string | null;
}

export interface FinanceContext {
  status: string;
  notices: Notice[];
  baseCurrency: string;
  basis: Record<string, string>;
  fx: Record<string, string>;
  cutover?: Cutover;
  generatedAt?: string;
  timezone?: string;
  comparisonStatus?: string;
}

export interface Compared {
  current: MoneyLine;
  previous: MoneyLine;
  basis?: string;
}

export type MaybeSuppressed<T> = Suppressed | (T & { suppressed: false });

export function isSuppressed<T>(value: MaybeSuppressed<T> | null | undefined): value is Suppressed {
  return !!value && (value as { suppressed?: boolean }).suppressed === true;
}

/** What is owed as of a day — Finance's own aging, in the base currency. */
export interface ReceivablesNow {
  asOf: string;
  currency: string;
  total: number;
  overdue: number;
  overdueSharePercent: number | null;
  buckets: Array<{ bucket: string; amount: number }>;
  unconverted: string[];
  complete: boolean;
}

/** A balance on a day: per currency, or why there is none. */
export interface CashStock {
  status: string;
  asOf: string;
  byCurrency?: Array<{ currency: string; amount: string }>;
}

export interface FinanceMonth {
  month: string;
  revenue: number | null;
  expenses: number | null;
  profit: number | null;
  complete: boolean;
  coverage: MonthCoverage;
}

export interface FinanceOverview {
  context: FinanceContext;
  standing: string;
  cards: {
    revenue: Compared;
    invoicesIssued: Compared;
    operatingExpenses: Compared;
    salaries: MaybeSuppressed<Compared>;
    otherExpenses: MaybeSuppressed<Compared>;
    netResult: Compared;
    receivables: ReceivablesNow;
    cash: CashStock;
  };
  months: FinanceMonth[];
  coverage: { months: number; notProvided: number; overlapChanged: number; missingDocuments: number; drafts: number };
}

export interface FinanceTrends {
  period: Period;
  context: FinanceContext;
  rows: Array<{ month: string; revenue: MoneyLine; expenses: MoneyLine; result: MoneyLine; coverage: MonthCoverage }>;
  total: { revenue: MoneyLine; expenses: MoneyLine; result: MoneyLine };
}

export interface CurrencyAmount {
  currency: string;
  amount: number;
}

export interface OverdueInvoice {
  id: string;
  number: string;
  client: ClientRef | null;
  project: ProjectRef | null;
  dueDate: string;
  daysOverdue: number;
  currency: string;
  remaining: number;
  remainingBase: number | null;
  status: string;
}

export interface ContractBilling {
  horizonDays: number;
  dueNotInvoiced: CurrencyAmount[];
  upcoming: CurrencyAmount[];
  periods: Array<{ contractId: string; number: string; client: ClientRef | null; start: string; end: string; amount: number; currency: string; state: string }>;
}

interface ForecastBase {
  method: string;
  window: { from: string; to: string };
  sample: number;
  minimumSample: number;
  medianDaysLate: number | null;
  p75DaysLate: number | null;
}

export type Forecast =
  | (ForecastBase & { available: false })
  | (ForecastBase & { available: true; paidByDueDatePercent: number; expectedNext30: number; confidence: 'LOW' | 'MEDIUM' | 'HIGH'; currency: string });

export interface ReceivablesFull {
  asOf: string;
  currency: string;
  unconverted: string[];
  aging: Array<{ bucket: string; amount: number }>;
  total: number;
  byClient: Array<{ client: ClientRef; total: number; overdue: number; currencies: string[] }>;
  concentration: { client: ClientRef; sharePercent: number } | null;
  overdue: OverdueInvoice[];
  overdueCount: number;
  expected: {
    overdueCollectible: number;
    dueNext7: number;
    dueNext30: number;
    dueNext90: number;
    contractBilling: ContractBilling;
    subscriptionBilling: Array<{ id: string; number: string; client: ClientRef | null; date: string | null; currency: string; amount: number }>;
  };
  forecast: Forecast;
}

export interface Budget {
  id: string;
  name: string;
  currency: string;
  amount: number;
  spent: number;
  percentUsed: number;
  thresholdExceeded: boolean;
  limitExceeded: boolean;
  startDate: string;
  endDate: string;
}

export interface FinanceExpenses {
  period: Period;
  context: FinanceContext;
  total: { current: MoneyLine; previous: MoneyLine };
  salaries: MaybeSuppressed<{ current: MoneyLine; historical: MoneyLine }>;
  other: MaybeSuppressed<{ current: MoneyLine; historical: MoneyLine }>;
  byCategory: Array<{ category: { id: string; code: string; name: string; nameAr: string | null } | null; amount: MoneyLine }>;
  budgets: Budget[];
  coverage: MonthCoverage[];
}

export interface ExpiringContract {
  id: string;
  number: string;
  title: string;
  client: ClientRef | null;
  project: ProjectRef | null;
  endDate: string;
  daysLeft: number;
}

type AgingKey = 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90_plus';
export const SUBSCRIPTION_AGING: AgingKey[] = ['current', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'];

export interface ContractsSubscriptions {
  asOf: string;
  subscriptions: {
    counts: { total: number; active: number; pastDue: number; cancelled: number; ending: number; monthly: number; yearly: number };
    recurring: {
      byCurrency: Array<{ currency: string; subscriptions: number; mrr: number; arr: number }>;
      base: { currency: string; mrr: number; arr: number; unconvertedCurrencies: string[] };
    };
    trend: Array<{ month: string; started: number; cancelled: number }>;
    billing: {
      byCurrency: Array<{ currency: string; invoiced: number; invoices: number; collected: number; payments: number; outstanding: number }>;
      collectedBase: { currency: string; amount: number };
    };
    aging: Array<{ currency: string } & Partial<Record<AgingKey, { amount: number; invoices: number }>>>;
    upcoming: Array<{
      id: string;
      number: string;
      nextBillingDate: string | null;
      billingCycle: string;
      currency: string;
      netPrice: number;
      client: ClientRef | null;
      package: { name: string; nameAr: string | null } | null;
    }>;
  };
  contracts: {
    active: number;
    waitingToStart: number;
    expiringWithinDays: number;
    expiring: ExpiringContract[];
    billing: ContractBilling;
    faceValueIsNotRevenue: boolean;
  };
}

export const AGING_BUCKETS = ['CURRENT', 'D1_30', 'D31_60', 'D61_90', 'D90_PLUS'] as const;

/** A total the API sends as a plain number with its completeness, as Finance's money line. */
export function lineOf(amount: number | null, currency: string, complete: boolean, unconverted: string[] = []): MoneyLine {
  return { base: complete && amount !== null ? amount : null, currency, complete: complete && amount !== null, unconverted, atTodayRate: false, byCurrency: [], count: 0 };
}

// ---- months ------------------------------------------------------------------------------------

/** A month as the trend shows it: unknown parts are null (a gap), never zero. */
export interface TrendRow {
  month: string;
  revenue: number | null;
  expenses: number | null;
  result: number | null;
  /** COMPLETE, PARTIAL (before the cutover, or with nothing provided) or NOT_RECONCILED (may count twice). */
  state: MonthCoverage['profit'];
  partialMonth: boolean;
}

/**
 * A month's figures with Finance's coverage applied: what was not provided is unknown, never zero; a
 * total that could not be valued in the base currency is unknown too.
 */
export function trendRowOf(month: string, revenue: number | null, expenses: number | null, result: number | null, coverage: MonthCoverage | null | undefined): TrendRow {
  const revenueUnknown = coverage?.revenue === 'NOT_PROVIDED';
  const expensesUnknown = coverage?.salaries === 'NOT_PROVIDED' || coverage?.otherExpenses === 'NOT_PROVIDED';
  return {
    month,
    revenue: revenueUnknown ? null : revenue,
    expenses: expensesUnknown ? null : expenses,
    result: revenueUnknown || expensesUnknown ? null : result,
    state: coverage?.profit ?? 'PARTIAL',
    partialMonth: coverage?.partialMonth ?? false,
  };
}

/** The months of an overview (live or stored), as the trend reads them. */
export function rowsFromMonths(months: FinanceMonth[] | null | undefined): TrendRow[] {
  return (months ?? []).map((m) => trendRowOf(m.month, m.complete ? m.revenue : null, m.complete ? m.expenses : null, m.complete ? m.profit : null, m.coverage));
}

/** Finance's twelve months, as the trend reads them. */
export function rowsFromTrends(rows: FinanceTrends['rows'] | null | undefined): TrendRow[] {
  return (rows ?? []).map((r) => trendRowOf(r.month, r.revenue.base, r.expenses.base, r.result.base, r.coverage));
}

// ---- pieces ------------------------------------------------------------------------------------

const COVERAGE_TONE: Record<string, BadgeTone> = {
  COMPLETE: 'success',
  PARTIAL: 'warning',
  NOT_RECONCILED: 'error',
  RECORDED: 'success',
  LIVE: 'success',
  HISTORICAL: 'info',
  HISTORICAL_AND_LIVE: 'info',
  OVERLAP_CHANGED: 'error',
  NO_RECORDS: 'neutral',
  NOT_PROVIDED: 'warning',
};

/** One of Finance's coverage states, in words. */
export function CoverageBadge({ state }: { state: string }) {
  const { t, has } = useI18n();
  const key = `finance.coverage.${state}`;
  return <Badge tone={COVERAGE_TONE[state] ?? 'neutral'}>{has(key) ? t(key) : state}</Badge>;
}

/** What a Finance status means, in a sentence. */
export function useTrustHint() {
  const { t, has } = useI18n();
  return (status: string | null | undefined) => {
    const key = `finance.statusHint.${status ?? ''}`;
    return has(key) ? t(key) : '';
  };
}

/**
 * Finance's notices with what they name — the months, the cutover day, the currencies. Kept apart from the
 * kit's list so a notice's details are said too.
 */
export function NoticeList({ notices }: { notices: Notice[] | null | undefined }) {
  const { t, has } = useI18n();
  const day = useDay();
  const monthName = useMonthName();
  if (!notices?.length) return null;
  const detailOf = (n: Notice): { label: TranslationKey; value: string } | null => {
    const d = (n.details ?? {}) as Record<string, unknown>;
    if (Array.isArray(d.months) && d.months.length) return { label: 'notice.detail.months', value: d.months.map((m) => monthName(String(m), true)).join(' · ') };
    if (typeof d.cutoverDate === 'string') return { label: 'notice.detail.cutover', value: day(d.cutoverDate) };
    if (Array.isArray(d.currencies) && d.currencies.length) return { label: 'notice.detail.currencies', value: d.currencies.map((c) => (typeof c === 'string' ? c : String((c as { currency?: string }).currency ?? ''))).join(', ') };
    if (typeof d.invoices === 'number') return { label: 'notice.detail.invoices', value: String(d.invoices) };
    if (Array.isArray(d.records) && d.records.length) {
      const months = d.records.map((r) => (r as { month?: string }).month).filter((m): m is string => !!m);
      return months.length ? { label: 'notice.detail.months', value: [...new Set(months)].map((m) => monthName(m, true)).join(' · ') } : null;
    }
    return null;
  };
  return (
    <ul className="space-y-1.5">
      {notices.map((n, i) => {
        const key = `notice.${n.code}`;
        const detail = detailOf(n);
        return (
          <li key={`${n.code}-${i}`} className="flex items-start gap-2 text-caption text-muted">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-fg dark:text-warning-dark" aria-hidden />
            <span>
              {has(key) ? t(key) : n.code}
              {detail && (
                <span className="ms-1 text-[var(--text-tertiary)]">
                  {t(detail.label)} <bdi>{detail.value}</bdi>
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Finance's status of the figures on a page, what it means, and its notices. */
export function FinanceTrust({ context, compact = false }: { context: FinanceContext | null | undefined; compact?: boolean }) {
  const { t } = useI18n();
  const hint = useTrustHint();
  if (!context) return null;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-body-sm">
        <span className="text-[var(--text-tertiary)]">{t('finance.trust.statusLabel')}</span>
        <TrustBadge status={context.status} />
        {!compact && <span className="text-caption text-muted">{hint(context.status)}</span>}
      </div>
      <NoticeList notices={context.notices} />
    </div>
  );
}

/** A line that says the figures are in the base currency, and which. */
export function InBase({ currency }: { currency: string }) {
  const { t } = useI18n();
  return (
    <span className="inline-flex items-center gap-1 text-caption text-muted">
      <Info className="h-3.5 w-3.5" aria-hidden />
      {t('finance.inBase', { currency })}
    </span>
  );
}
